/*
 *  -----------------------------------------------------------------------
 *    <copyright file="evaluation-models-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';

import ExpandableGrid from '@components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from '@components/core/grid-filters';
import { useTranslation, useGridCells } from '@hooks/index';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';

import './styles.scss';

const EvaluationModelsGrid = ({ data, rowActions }: any) => {
    const { t } = useTranslation();
    const { YesNoCell, DateCell, MaxLengthCell } = useGridCells();
    const [models, setModels] = useState([]);

    useEffect(() => {
        if (data) {
            const newData = restructuredUpdatedCreatedBy(data);
            const arrayToSort =
                orderBy(newData, [
                    { field: 'active', dir: 'desc' },
                    { field: 'updated', dir: 'desc' },
                ]) ?? [];
            setModels(arrayToSort);
        }
    }, [data]);

    return (
        <ExpandableGrid
            customKey="eval-models-grid"
            id="eval-models"
            items={models}
            rowActions={rowActions}
            columns={[
                {
                    label: t('active'),
                    id: 'risk-active',
                    field: 'active',
                    minWidth: 140,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:evaluationModelName'),
                    id: 'risk-name',
                    field: 'name',
                    minWidth: 180,
                },
                {
                    label: t('ra:includeDetectability'),
                    id: 'risk-detectability',
                    field: 'includeDetectability',
                    minWidth: 240,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:likelihoodUpperLimit'),
                    id: 'risk-likelihood-upper',
                    field: 'likelihoodUpper',
                    minWidth: 240,
                    filter: 'numeric',
                },
                {
                    label: t('ra:impactUpperLimit'),
                    id: 'risk-impact-upper',
                    field: 'impactUpper',
                    minWidth: 240,
                    filter: 'numeric',
                },
                {
                    label: t('ra:detectabilityUpperLimit'),
                    id: 'risk-detectability-upper',
                    field: 'detectabilityUpper',
                    minWidth: 260,
                    filter: 'numeric',
                },
                {
                    label: t('ra:createdDate'),
                    field: 'created',
                    id: 'risk-created',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    label: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'risk-createdby',
                    minWidth: 200,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'risk-updated',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    label: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'risk-updatedby',
                    minWidth: 200,
                },
                {
                    label: t('id'),
                    id: 'risk-id',
                    field: 'recordId',
                    minWidth: 100,
                    filter: 'numeric',
                    showByDefault: false,
                },
            ]}
            groupable={false}
            showPager
            scrollable="scrollable"
            pageable={false}
            resizable
            reorderable
            addScroll
            options={{
                allowExcelExport: false,
                allowExpandCollapseAll: false,
                useFilterChips: true,
            }}
            detail={null}
        />
    );
};

export default EvaluationModelsGrid;
